import type { ContactOrder } from "../../../../../../../../../../../../../api/kehrblatt/contact-orders"
import type {
  InboxCaseBatchWorkflow,
  InboxCaseBatchWorkflowItem,
} from "../../../../../../../../../../../../../api/kehrblatt/inbox-cases"
import type { ScheduleEventStatus } from "../../../../../../../../../../../../../_shared/_logic/enums"
import type {
  InformCustomerRecipientSummary,
  InformCustomerSourcePreview,
  InformPropertySummary,
  InformScheduleEventSummary,
  InformWorkOrderSummary,
} from "../contract"
import { customerInformationOutputForItem, projectPersistedCustomerInformation } from "./outputs"

export type PlannedAppointmentStepOutput = {
  work_order_id: string
  schedule_event_id: string | null
  source_schedule_event_id: string | null
  canceled_schedule_event_id: string | null
  status: string | null
  start_at: string | null
  end_at: string | null
  employee_id: string | null
  employee_name: string | null
  appointment_contact_customer_id: string | null
  contact_order_id: string | null
  first_contact_step_id: string | null
  effective_planning_strategy: string | null
  decision: string | null
  reason: string | null
}

export type InboxContactOrderSourceRow = {
  actionCaseId: string
  workOrder: WorkOrder
  event: ScheduleEvent
  planned: PlannedAppointmentStepOutput
  requesterCustomerId: string
  targetCustomerIds: string[]
  contactOrderReason: "INITIAL" | "RESCHEDULE"
}

export type InformCustomerSourceData = {
  rows: InboxContactOrderSourceRow[]
  workOrders: WorkOrder[]
  events: ScheduleEvent[]
  properties: Property[]
  customers: Customer[]
  contactPoints: ContactPoint[]
  contactOrders: ContactOrder[]
  errors: Array<{ actionCaseId: string | null; message: string }>
}

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === "object" && !Array.isArray(value))

export const objectValue = (value: unknown): Record<string, unknown> =>
  isRecord(value) ? value : {}

export const stringValue = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim()

    if (typeof value === "number" && Number.isFinite(value)) return String(value)
  }

  return ""
}

const outputFor = (item: InboxCaseBatchWorkflowItem, stepKey: string) =>
  objectValue(item.steps.find((step) => step.step_key === stepKey)?.output)

const plannedOutput = (item: InboxCaseBatchWorkflowItem): PlannedAppointmentStepOutput | null => {
  const stepOutput = outputFor(item, "plan_appointment")
  const output = objectValue(stepOutput.planned_appointment || stepOutput)
  const workOrderId = stringValue(output.work_order_id)

  if (!workOrderId) return null

  return {
    work_order_id: workOrderId,
    schedule_event_id: stringValue(output.schedule_event_id) || null,
    source_schedule_event_id: stringValue(output.source_schedule_event_id) || null,
    canceled_schedule_event_id: stringValue(output.canceled_schedule_event_id) || null,
    status: stringValue(output.status) || null,
    start_at: stringValue(output.start_at) || null,
    end_at: stringValue(output.end_at) || null,
    employee_id: stringValue(output.employee_id) || null,
    employee_name: stringValue(output.employee_name) || null,
    appointment_contact_customer_id: stringValue(output.appointment_contact_customer_id) || null,
    contact_order_id: stringValue(output.contact_order_id) || null,
    first_contact_step_id: stringValue(output.first_contact_step_id) || null,
    effective_planning_strategy: stringValue(output.effective_planning_strategy) || null,
    decision: stringValue(output.decision) || null,
    reason: stringValue(output.reason) || null,
  }
}

const plannedOutputFromPreview = (
  context: InformCustomerSourcePreview["rowContextByActionCaseId"][string],
  event: InformScheduleEventSummary,
): PlannedAppointmentStepOutput => ({
  work_order_id: context.workOrderId,
  schedule_event_id: context.scheduleEventId,
  source_schedule_event_id: context.sourceScheduleEventId,
  canceled_schedule_event_id: context.canceledScheduleEventId,
  status: event.status || null,
  start_at: event.startAt || null,
  end_at: event.endAt || null,
  employee_id: event.employeeId,
  employee_name: event.employeeName || null,
  appointment_contact_customer_id: context.requesterCustomerId,
  contact_order_id: null,
  first_contact_step_id: null,
  effective_planning_strategy:
    context.sourceScheduleEventId === context.scheduleEventId && !context.canceledScheduleEventId
      ? "KEEP_EXISTING"
      : "REPLAN",
  decision: "PLANNED",
  reason: null,
})

const propertyView = (property: InformPropertySummary): Property =>
  ({
    id: property.id,
    street: property.street,
    house_number: property.houseNumber || null,
    zip: property.postalCode || null,
    city: property.city || null,
    country: property.country || null,
    address_extra: property.addressExtra || null,
    address_label: property.addressLabel,
    label: property.addressLabel,
    units: [],
  }) as unknown as Property

const recipientLink = (recipient: InformCustomerRecipientSummary) => ({
  customer_id: recipient.customerId,
  customer_label: recipient.displayName,
  role: recipient.role || null,
})

const displayOrderTypes = (
  workOrder: Pick<InformWorkOrderSummary, "orderTypeIds" | "orderTypeLabel">,
): WorkOrderOrderTypeItem[] => {
  const label = workOrder.orderTypeLabel.trim()
  const ids = workOrder.orderTypeIds

  if (!ids.length) {
    return label
      ? [{ order_type_id: null, internal_label: label, public_label: label, description: null }]
      : []
  }

  if (ids.length === 1) {
    const itemLabel = label || ids[0]!

    return [
      {
        order_type_id: ids[0]!,
        internal_label: itemLabel,
        public_label: itemLabel,
        description: null,
      },
    ]
  }

  const rawParts = label
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
  const labels =
    rawParts.length >= ids.length
      ? [...rawParts.slice(0, ids.length - 1), rawParts.slice(ids.length - 1).join(", ")]
      : ids.map((id, index) => rawParts[index] ?? id)

  return ids.map((id, index) => ({
    order_type_id: id,
    internal_label: labels[index]!,
    public_label: labels[index]!,
    description: null,
  }))
}

const workOrderView = (
  workOrder: InformWorkOrderSummary,
  recipients: InformCustomerRecipientSummary[],
): WorkOrder =>
  ({
    id: workOrder.id,
    property_id: workOrder.propertyId,
    property_unit_id: workOrder.propertyUnitId,
    property_unit_label: workOrder.propertyUnitLabel || null,
    order_number: workOrder.orderNumber || null,
    order_types: displayOrderTypes(workOrder),
    due_date: workOrder.dueDate,
    estimated_duration_min: workOrder.estimatedDurationMin,
    assigned_employee_id: workOrder.assignedEmployeeId,
    allowed_employee_ids: workOrder.allowedEmployeeIds,
    current_schedule_event_id: workOrder.currentScheduleEventId,
    status: workOrder.status,
    recipients: recipients.map(recipientLink),
  }) as unknown as WorkOrder

const eventView = ({
  event,
  workOrder,
  planned,
  organizationId,
  confirmationInformation,
}: {
  event: InformScheduleEventSummary
  workOrder: WorkOrder
  planned: PlannedAppointmentStepOutput
  organizationId: string
  confirmationInformation: boolean
}): ScheduleEvent =>
  ({
    id: event.id,
    organization_id: organizationId,
    employee_id: event.employeeId,
    employee_name: event.employeeName || null,
    event_type: "APPOINTMENT",
    flex_type: event.flexType ?? "HARD",
    status: event.status as ScheduleEventStatus,
    planning_flag: "OK",
    start_at: event.startAt,
    end_at: event.endAt,
    timeframe_start_at: event.timeframeStartAt ?? event.startAt,
    timeframe_end_at: event.timeframeEndAt ?? event.endAt,
    customer_window_start_at: event.customerWindowStartAt,
    customer_window_end_at: event.customerWindowEndAt,
    all_day: false,
    property_id: event.propertyId ?? workOrder.property_id,
    work_order_id: event.workOrderId ?? workOrder.id,
    source: "SYSTEM",
    contact_order_id: planned.contact_order_id,
    ...(confirmationInformation
      ? {
          customer_information_mode: "confirmation_pending",
          customer_information_label: "Bestätigung wird per E-Mail übermittelt",
          customer_information_detail: null,
        }
      : {}),
  }) as ScheduleEvent

const customerView = (
  recipient: InformCustomerRecipientSummary,
  organizationId: string,
): Customer =>
  ({
    id: recipient.customerId,
    organization_id: organizationId,
    display_name: recipient.displayName,
    name: recipient.displayName,
    role: recipient.role || null,
  }) as unknown as Customer

const contactPointViews = (
  recipient: InformCustomerRecipientSummary,
  organizationId: string,
): ContactPoint[] =>
  [
    recipient.email
      ? {
          id: `preview-email:${recipient.customerId}`,
          organization_id: organizationId,
          customer_id: recipient.customerId,
          kind: "EMAIL",
          value: recipient.email,
          lifecycle_status: "VERIFIED",
          is_verified: true,
          is_primary: true,
        }
      : null,
    recipient.phone && recipient.phoneKind
      ? {
          id: `preview-phone:${recipient.customerId}`,
          organization_id: organizationId,
          customer_id: recipient.customerId,
          kind: recipient.phoneKind,
          value: recipient.phone,
          lifecycle_status: "VERIFIED",
          is_verified: true,
          is_primary: true,
        }
      : null,
  ].filter((value): value is NonNullable<typeof value> => value !== null) as ContactPoint[]

export const buildInformCustomerSourceData = ({
  workflow,
  preview,
  organizationId,
}: {
  workflow: InboxCaseBatchWorkflow
  preview: InformCustomerSourcePreview | null
  organizationId: string
}): InformCustomerSourceData => {
  if (!preview) {
    return {
      rows: [],
      workOrders: [],
      events: [],
      properties: [],
      customers: [],
      contactPoints: [],
      contactOrders: [],
      errors: [{ actionCaseId: null, message: "Kundeninformationsdaten fehlen." }],
    }
  }

  const rows: InboxContactOrderSourceRow[] = []
  const errors = Object.entries(preview.rowErrorsByActionCaseId).map(([actionCaseId, message]) => ({
    actionCaseId,
    message,
  }))
  const workOrders = new Map<string, WorkOrder>()
  const events = new Map<string, ScheduleEvent>()
  const contactOrders = new Map<string, ContactOrder>()
  const properties = new Map<string, Property>()
  const recipients = new Map<string, InformCustomerRecipientSummary>()
  const relevantItems = workflow.items.filter(
    (item) =>
      item.compatible &&
      (item.steps.some((step) => step.step_key === "inform_customer") ||
        item.action_case.requirements.includes("INFORM_CUSTOMER")),
  )

  for (const item of relevantItems) {
    const actionCaseId = item.action_case.id
    const context = preview.rowContextByActionCaseId[actionCaseId]

    if (!context) {
      if (!preview.rowErrorsByActionCaseId[actionCaseId]) {
        errors.push({
          actionCaseId,
          message: "Kundeninformationskontext fehlt für diese Inbox-Zeile.",
        })
      }

      continue
    }

    const workOrderSummary = preview.workOrdersById[context.workOrderId]
    const propertySummary = preview.propertiesById[context.propertyId]
    const eventSummary = preview.scheduleEventsById[context.scheduleEventId]
    const planned = plannedOutput(item) ?? plannedOutputFromPreview(context, eventSummary)
    const rowRecipients = context.targetCustomerIds
      .map((customerId) => preview.recipientsById[customerId])
      .filter((recipient): recipient is InformCustomerRecipientSummary => Boolean(recipient))
      .map((recipient) =>
        recipient.customerId === context.requesterCustomerId && !recipient.role
          ? { ...recipient, role: context.requesterRole }
          : recipient,
      )

    if (
      !workOrderSummary ||
      !propertySummary ||
      !eventSummary ||
      !planned ||
      !rowRecipients.length
    ) {
      errors.push({
        actionCaseId,
        message: "Kundeninformationsdaten sind für diese Inbox-Zeile unvollständig.",
      })
      continue
    }

    const workOrder = workOrderView(workOrderSummary, rowRecipients)
    const actionPayload = objectValue(item.action_case.payload)
    const confirmationInformation =
      actionPayload.appointment_proposal_ref !== null &&
      planned.effective_planning_strategy === "KEEP_EXISTING" &&
      !context.canceledScheduleEventId
    const baseEvent = eventView({
      event: eventSummary,
      workOrder,
      planned,
      organizationId,
      confirmationInformation,
    })
    const customerInformationOutput = customerInformationOutputForItem(item)
    const contactOrderReason = context.canceledScheduleEventId ? "RESCHEDULE" : "INITIAL"
    const persistedInformation = customerInformationOutput
      ? projectPersistedCustomerInformation({
          output: customerInformationOutput,
          actionCaseId,
          organizationId,
          workOrder,
          event: baseEvent,
          contactOrderReason,
        })
      : { event: baseEvent, contactOrder: null }
    const event = persistedInformation.event

    if (persistedInformation.contactOrder) {
      contactOrders.set(persistedInformation.contactOrder.id, persistedInformation.contactOrder)
    }

    workOrders.set(workOrder.id, workOrder)
    events.set(event.id, event)
    properties.set(propertySummary.id, propertyView(propertySummary))
    rowRecipients.forEach((recipient) => recipients.set(recipient.customerId, recipient))
    rows.push({
      actionCaseId,
      workOrder,
      event,
      planned: {
        ...planned,
        source_schedule_event_id: context.sourceScheduleEventId,
        canceled_schedule_event_id: context.canceledScheduleEventId,
      },
      requesterCustomerId: context.requesterCustomerId,
      targetCustomerIds: context.targetCustomerIds,
      contactOrderReason,
    })
  }

  const recipientValues = [...recipients.values()]

  return {
    rows,
    workOrders: [...workOrders.values()],
    events: [...events.values()],
    properties: [...properties.values()],
    customers: recipientValues.map((recipient) => customerView(recipient, organizationId)),
    contactPoints: recipientValues.flatMap((recipient) =>
      contactPointViews(recipient, organizationId),
    ),
    contactOrders: [...contactOrders.values()],
    errors,
  }
}
